import { Location } from '@angular/common';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService } from 'app/modules/session/services/authentication.service';
import { Observable, throwError, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

/**
 * Ensures any valid LendioResponse objects returned from the Lendio API are handled correctly by the HttpClient and
 * mapped to Observable events as defined by the HttpClient API.
 */
export class ApiResponseInterceptor implements HttpInterceptor {

  constructor(
    private location: Location,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    return next.handle(req).pipe(
      catchError(error => {
        // Suppress ProgressEvent errors as they aren't informative.
        if (error instanceof ProgressEvent) {
          return of();
        }

        return throwError(() => this.mapErrorResponse(error));
      }),
      switchMap(event => {
        if (event instanceof HttpResponse) {
          return this.mapResponse(event) as Observable<HttpEvent<any>>;
        }
        return of(event);
      })
    );
  }

  /**
   * Maps a success response such that the "data" property of the response object is returned if possible,
   * otherwise the entire response object is returned.
   *
   * @param response The HttpResponse instance from the request.
   */
  private mapResponse(response: HttpResponse<any>) {
    const body = response.body;

    // Switch to an error if the status was not 'success'
    if (body && body.status && body.status.toLowerCase() !== 'success') {
      return throwError(() => this.mapErrorResponse(new HttpErrorResponse({
        error: body,
        headers: response.headers,
        status: response.status,
        statusText: response.statusText,
        url: response.url || undefined,
      })));
    }

    // Return the 'data' property of the response on success if it exists.
    // If links exists (pagination), don't filter them out.
    if (body && body.hasOwnProperty('data') && !body.hasOwnProperty('links')) {
      return of(new HttpResponse({
        body: body.data,
        headers: response.headers,
        status: response.status,
        statusText: response.statusText,
        url: response.url || undefined
      }));
    }

    return of(response);
  }

  /**
   * Returns an error observable containing the value pulled from the body of the HTTP response. We attempt
   * to return the value from the "errors" property if it exists.
   * also navigates the user to the login page or Okta (if not localhost) if any response from API returns a 401
   *
   * @param errorResponse HttpErrorResponse instance returned from HttpBackend interceptor.
   */
  private mapErrorResponse(errorResponse: HttpErrorResponse): any {
    const path = this.location.path();
    const authService = new AuthenticationService();
    if(errorResponse?.error?.message === 'Access token is not valid') {
      authService.clearAuthCookies();
    }
    if (errorResponse?.status === 401 && path !== '/login' && !path.includes('token')) {
      authService.redirectToAuthenticate();
    }
    const body = errorResponse.error;
    if (body && body.errors) {
      if (body.errors.length === 1) {
        return body.errors[0];
      }
      return body.errors;
    }
    return body;
  }
}
